import { useEffect, useRef } from 'react'

/**
 * Adds `is-visible` to the element the first time it scrolls into view, which
 * drives the `.reveal` transition in index.css.
 *
 * Framer Motion already handles page transitions; this is for the long
 * marketing pages, where mounting dozens of motion components to animate a
 * one-shot entrance costs more than it is worth.
 *
 * Two deliberate safety properties, because the failure mode here is content
 * that never becomes visible:
 *
 *   1. The hidden start state lives behind `.reveal-enabled` on <html>, which
 *      is only set once we know we have an observer and motion is wanted. No
 *      script, no observer, or a thrown error all leave the content visible.
 *   2. A watchdog reveals the element regardless after WATCHDOG_MS. Observers
 *      have been seen not to fire in embedded and headless browsers even when
 *      the element is plainly on screen; an animation is not worth a blank
 *      page.
 */

const WATCHDOG_MS = 2600

function motionWanted() {
  if (typeof window === 'undefined' || !window.matchMedia) return true
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

let enabled = false

/** Arm the hidden start state once, on first use. */
function enableRevealOnce() {
  if (enabled || typeof document === 'undefined') return
  enabled = true
  document.documentElement.classList.add('reveal-enabled')
}

export default function useReveal({ threshold = 0.16, rootMargin = '0px 0px -8% 0px' } = {}) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const show = () => el.classList.add('is-visible')

    // No observer, or motion is unwanted: leave it visible and do nothing.
    if (typeof IntersectionObserver === 'undefined' || !motionWanted()) {
      show()
      return
    }

    enableRevealOnce()

    let observer
    // If anything about the observer misbehaves, fall back to showing it.
    const watchdog = window.setTimeout(() => {
      show()
      observer?.disconnect()
    }, WATCHDOG_MS)

    try {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue
            show()
            window.clearTimeout(watchdog)
            observer.unobserve(entry.target)
          }
        },
        { threshold, rootMargin }
      )
      observer.observe(el)
    } catch {
      window.clearTimeout(watchdog)
      show()
      return
    }

    return () => {
      window.clearTimeout(watchdog)
      observer?.disconnect()
    }
  }, [threshold, rootMargin])

  return ref
}
